import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { useCartStore } from '@/features/Cart/stores/cart';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import type { ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type { ProductComplete } from '@/features/Product/types/models';
import type { StockItem } from '@/features/Product/services/productAvailability';

type UseCartMaxQuantityOptions = {
  product?: MaybeRefOrGetter<ProductComplete | undefined | null>;
  variant?: MaybeRefOrGetter<ProductComplete | undefined | null>;
  cartItem?: MaybeRefOrGetter<ShopifyCartItem | undefined | null>;
  variantIds?: MaybeRefOrGetter<number[]>;
  fallbackMax?: number;
  isFreeGift?: MaybeRefOrGetter<boolean>;
  stockItem?: MaybeRefOrGetter<StockItem | undefined>;
};

const getPropertyMaxCartQuantity = (cartItem?: ShopifyCartItem | null) => {
  const propertyMax = Number(cartItem?.properties?._max_cart_quantity);
  if (Number.isFinite(propertyMax) && propertyMax > 0) {
    return propertyMax;
  }
  return undefined;
};

export const useCartMaxQuantity = (options: UseCartMaxQuantityOptions) => {
  const cartStore = useCartStore();
  const { getAddableQuantity, getItemStockLimit } = useProductStockStore();
  const fallbackMax = computed(() => options.fallbackMax ?? 5);
  const configuredMaxCartQuantity = computed(() => {
    if (toValue(options.isFreeGift)) return 1;
    const cartItemMax = getPropertyMaxCartQuantity(toValue(options.cartItem));
    if (cartItemMax) return cartItemMax;
    return (
      toValue(options.variant)?.attributes.maxCartQuantity ??
      toValue(options.product)?.attributes.maxCartQuantity ??
      fallbackMax.value
    );
  });

  const variantIdsToCheck = computed(() => {
    const variantIds = toValue(options.variantIds);
    if (variantIds?.length) return variantIds;
    const variant = toValue(options.variant);
    const product = toValue(options.product);
    return [variant?.attributes.variantId, product?.attributes.variantId].filter(
      (id): id is number => typeof id === 'number',
    );
  });

  const currentCartQuantity = computed(() => {
    const ids = variantIdsToCheck.value;
    if (!ids.length) return 0;
    return cartStore.cartData.items
      .filter((item) => ids.includes(item.variant_id))
      .reduce((total, item) => total + item.quantity, 0);
  });

  const stockAddableQuantity = computed<number | undefined>(() => {
    const [variantId] = variantIdsToCheck.value;
    const stockItem =
      toValue(options.stockItem) ?? (variantId === undefined ? undefined : { variantId });
    return stockItem ? getItemStockLimit(stockItem) : undefined;
  });

  const stockMaxCartQuantity = computed<number | undefined>(() => {
    const cartItem = toValue(options.cartItem);
    if (cartItem) {
      const addableQuantity = getAddableQuantity(
        { variantId: cartItem.variant_id, properties: cartItem.properties },
        cartItem.key,
      );
      return addableQuantity === undefined
        ? undefined
        : Math.max(addableQuantity, cartItem.quantity);
    }
    return stockAddableQuantity.value === undefined
      ? undefined
      : currentCartQuantity.value + stockAddableQuantity.value;
  });

  const maxCartQuantity = computed(() =>
    stockMaxCartQuantity.value === undefined
      ? configuredMaxCartQuantity.value
      : Math.min(configuredMaxCartQuantity.value, stockMaxCartQuantity.value),
  );

  const remainingCartQuantity = computed(() => {
    return Math.max(0, maxCartQuantity.value - currentCartQuantity.value);
  });

  const maxCartQuantityReached = computed(() => {
    return currentCartQuantity.value >= configuredMaxCartQuantity.value;
  });

  const clampToMaxCartQuantity = (quantity: number) => {
    if (!Number.isFinite(quantity)) return 0;
    return Math.min(Math.max(0, Math.floor(quantity)), remainingCartQuantity.value);
  };

  return {
    maxCartQuantity,
    stockAddableQuantity,
    currentCartQuantity,
    remainingCartQuantity,
    maxCartQuantityReached,
    clampToMaxCartQuantity,
  };
};
